/**
 * SF-05 Gloss uniformity (INV-CH-SF-05; kept). Language-agnostic (§2.12).
 *
 * Events per 1k prose words: parenthetical glosses "Term (translation)" ×1 and dash appositive
 * definitions "—the specialised methods of …" ×0.7.
 */
import { compiledEnglish, line, proseBlocks, rateDef, wordExposure } from './a-shared.ts';
import { makeEvidence, rateChannel } from './framework.ts';
import type { ChannelImpl, Measurement, RunContext } from './types.ts';

const W_DASH = 0.7;

export const sf05Def = rateDef({
	id: 'SF-05',
	name: 'Gloss uniformity',
	family: 'FIGURE',
	features: [
		{ id: 'gloss', label: 'Parenthetical gloss' },
		{ id: 'dashGloss', label: 'Dash-appositive definition' },
	],
	oneWay: true,
	minExposure: 350,
	exposureUnit: 'words',
	lang: 'agnostic',
	ramp: [
		[0, 10],
		[3, 28],
		[8, 48],
		[14, 66],
		[22, 82],
		[35, 92],
	],
	unitScale: 1000,
	unitLabel: 'weighted glosses per 1k words',
	phi0: 1.5,
	weight: 0.8,
	nd: 0.15,
	changes: [],
});

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const nr = compiledEnglish().channels['ch-sf05'];
	const iGloss = nr.index.parentheticalGloss as number;
	const k = new Float64Array(doc.paragraphs.count);
	const e = wordExposure(doc);
	const ev = makeEvidence();
	let glosses = 0;
	let dashGlosses = 0;
	for (const b of proseBlocks(doc)) {
		nr.list.scanBlock(b.text, b.base, (s, en, i) => {
			if (i === iGloss) {
				glosses++;
				k[b.p] = (k[b.p] as number) + 1;
				ev.add(s, en, 0, 1);
			} else {
				dashGlosses++;
				k[b.p] = (k[b.p] as number) + W_DASH;
				ev.add(s, en, 1, W_DASH);
			}
		});
	}
	const summary: string[] = [];
	if (glosses)
		summary.push(line(`${glosses} parenthetical glosses`, 'Term (translation/definition) pattern'));
	if (dashGlosses)
		summary.push(
			line(
				`${dashGlosses} dash-appositive definition${dashGlosses === 1 ? '' : 's'}`,
				'"—the specialised methods of …"'
			)
		);
	return { k, e, evidence: ev, summary, features: { glosses, dashGlosses } };
}

export const sf05: ChannelImpl = rateChannel(sf05Def, measure);
export { sf05Def as def, sf05 as impl };
